// Data management utilities for retirement calculator
import { ref } from 'vue'
import { cloneObject } from '@/utilities'
import { defaultRetCalcData } from '@/lib/RetirementCalculatorHelper'
import type { RetirementData, IncomeAsset, CareerBreak } from './types'

export const useRetirementDataManager = () => {
  // Core data management functions
  const createDefaultRetirementData = (): RetirementData => {
    return cloneObject(defaultRetCalcData)
  }

  const createDefaultIncomeAsset = (): IncomeAsset => {
    return {
      type: null,
      current_value: null,
      income: null,
      frequency: 'yearly',
      shared: true,
    }
  }

  const createDefaultCareerBreak = (): CareerBreak => {
    return {
      age: null,
      duration: null,
    }
  }

  // Income frequency conversion utilities
  const convertIncomeToAnnual = (income: number, frequency: string): number => {
    switch (frequency) {
      case 'weekly':
        return income * 52
      case 'fortnightly':
        return income * 26
      case 'monthly':
        return income * 12
      default:
        return income
    }
  }

  const convertContributionToAnnual = (contribution: number, frequency: string): number => {
    return frequency === 'yearly' ? contribution : contribution * 12
  }

  // Data validation helpers
  const isValidRetirementData = (data: RetirementData): boolean => {
    return !!(data.age && data.income && data.current_super_balance !== null)
  }

  const resetRetirementData = (target: 'user' | 'partner' | 'all' = 'all') => {
    const defaultData = createDefaultRetirementData()
    
    switch (target) {
      case 'user':
        return { userData: defaultData }
      case 'partner':
        return { partnerData: defaultData }
      case 'all':
      default:
        return {
          userData: defaultData,
          partnerData: defaultData,
        }
    }
  }

  return {
    createDefaultRetirementData,
    createDefaultIncomeAsset,
    createDefaultCareerBreak,
    convertIncomeToAnnual,
    convertContributionToAnnual,
    isValidRetirementData,
    resetRetirementData,
  }
}